import React, { useState } from 'react';
import { Calendar, Download, Play, Clock } from 'lucide-react';
import { useWeekStartDay } from '../hooks/useWeekStartDay';

interface TimecardsAnalysisProps {
  onGenerateReport: (request: {
    reportType: 'timecards-analysis';
    parameters: {
      weekStartDate: string;
    };
  }) => void;
}

const TimecardsAnalysis: React.FC<TimecardsAnalysisProps> = ({ onGenerateReport }) => {
  const weekStartsOn = useWeekStartDay(); // Get week start day from settings
  const [selectedWeekStart, setSelectedWeekStart] = useState<string>('');

  // Get current week's start day based on settings
  const getCurrentWeekStart = () => {
    const today = new Date();
    const day = today.getDay();
    // Calculate difference to get to the week start day
    let diff = day - weekStartsOn;
    if (diff < 0) diff += 7; // Handle wrap-around
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - diff);
    return weekStart.toISOString().split('T')[0];
  };

  // Initialize with current week
  React.useEffect(() => {
    if (!selectedWeekStart && weekStartsOn !== undefined) {
      setSelectedWeekStart(getCurrentWeekStart());
    }
  }, [weekStartsOn]);

  const getWeekStartFromDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDay();
    // Calculate difference to get to the week start day
    let diff = day - weekStartsOn;
    if (diff < 0) diff += 7; // Handle wrap-around
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - diff);
    return weekStart.toISOString().split('T')[0];
  };

  const handleWeekChange = (direction: 'prev' | 'next') => {
    const currentDate = new Date(selectedWeekStart);
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));
    setSelectedWeekStart(newDate.toISOString().split('T')[0]);
  };

  const handleDateChange = (dateString: string) => {
    const weekStart = getWeekStartFromDate(dateString);
    setSelectedWeekStart(weekStart);
  };

  const getWeekEndDate = (startDate: string) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + 6);
    return date.toISOString().split('T')[0];
  };

  const formatWeekRange = (startDate: string) => {
    const start = new Date(startDate);
    const end = new Date(startDate);
    end.setDate(start.getDate() + 6);
    
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const handleGenerateReport = () => {
    onGenerateReport({
      reportType: 'timecards-analysis',
      parameters: {
        weekStartDate: selectedWeekStart
      }
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-neutral-900">Timecards Analysis</h2>
        <p className="text-sm text-neutral-600 mt-1">
          View detailed timecard data for employees for a selected week
        </p>
      </div>

      {/* Week Navigator and Report Info Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Week Navigator - Left Side (2/3 width) */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-neutral-200 p-6">
          <label className="block text-sm font-medium text-neutral-700 mb-3">
            <Calendar className="inline h-4 w-4 mr-2" />
            Select Week
          </label>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleWeekChange('prev')}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
            >
              ← Previous
            </button>
            
            <div className="flex-1 min-w-0">
              <input
                type="date"
                value={selectedWeekStart}
                onChange={(e) => handleDateChange(e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-md text-sm"
              />
              <div className="text-xs text-neutral-600 mt-1">
                {selectedWeekStart && formatWeekRange(selectedWeekStart)}
              </div>
            </div>
            
            <button
              onClick={() => handleWeekChange('next')}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
            >
              Next →
            </button>
          </div>
          
          {/* Generate Button - Below navigator */}
          <div className="mt-4">
            <button
              onClick={handleGenerateReport}
              disabled={!selectedWeekStart}
              className="w-full max-w-xs mx-auto flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-neutral-300 disabled:cursor-not-allowed transition-colors"
            >
              <Play className="h-4 w-4" />
              Generate Report
            </button>
          </div>
        </div>

        {/* Report Fields Preview - Right Side (1/3 width) */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 h-fit">
          <h3 className="text-sm font-semibold text-neutral-900 mb-3">Report Includes</h3>
          <div className="text-sm text-neutral-700 space-y-1.5">
            <div>• Employee Name</div>
            <div>• Date</div>
            <div>• Scheduled/Actual/Paid Times</div>
            <div>• Hours (Scheduled/Actual/Paid)</div>
            <div>• Break Minutes</div>
            <div>• Position Code</div>
            <div>• Approval Status</div>
          </div>
          
          <div className="mt-3 pt-3 border-t border-neutral-300">
            <p className="text-xs text-neutral-600">
              <span className="font-medium">Note:</span> Only <span className="font-medium text-green-700">approved timecards</span> included
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimecardsAnalysis;
